import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Building2, Crown, Loader2, LayoutDashboard, Users, Mail, Settings as Cog,
  UserPlus, X, Check, UserMinus, Trash2, Trophy, Clock, ChevronRight, UserCog, Network,
  ShieldCheck, Send, Award, BarChart3, Inbox, Globe, Plus, Eye, EyeOff,
} from "lucide-react";
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { toast } from "sonner";
import CandidateAgencyView from "./CandidateAgencyView";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const METRICS = ["optimizations", "applications", "shortlisted", "interviews", "offers", "rejections"];
const METRIC_LABELS = {
  optimizations: "Optimizations", applications: "Applications", shortlisted: "Shortlisted",
  interviews: "Interviews", offers: "Offers", rejections: "Rejections",
};
const CHART_COLORS = { optimizations: "#06b6d4", applications: "#6366f1", shortlisted: "#f59e0b", interviews: "#8b5cf6", offers: "#10b981", rejections: "#ef4444" };
const PUBLIC_METRICS = ["optimizations", "applications", "shortlisted", "interviews", "offers"];
const POLICY_FLAGS = [
  { k: "team_heads_see_org_totals", l: "See agency-wide totals", d: "Recruiters can view org-wide totals (read-only)." },
  { k: "team_heads_see_other_teams", l: "See other teams", d: "Recruiters can view teams they don't head." },
  { k: "team_heads_see_candidate_names", l: "See candidate names", d: "When off, candidates appear anonymised to recruiters." },
  { k: "recruiters_can_invite", l: "Recruiters can invite candidates", d: "Let recruiters invite candidates (not recruiters)." },
  { k: "team_heads_receive_reports", l: "Team heads receive email reports", d: "Email each team head their team's report. They can still mute it personally." },
];
const RANGES = [{ k: "day", l: "Today" }, { k: "week", l: "7d" }, { k: "15day", l: "15d" }, { k: "month", l: "30d" }, { k: "all", l: "All" }];
const FREQS = [{ k: "off", l: "Off" }, { k: "daily", l: "Daily" }, { k: "weekly", l: "Weekly" }];

const Toggle = ({ on, onClick, testid }) => (
  <button data-testid={testid} onClick={onClick}
    style={{ width: 44, height: 25, borderRadius: 999, border: "none", cursor: "pointer", flexShrink: 0,
      background: on ? "#6366f1" : "var(--border-main)", position: "relative", transition: "background 0.15s" }}>
    <span style={{ position: "absolute", top: 3, left: on ? 22 : 3, width: 19, height: 19, borderRadius: "50%", background: "#fff", transition: "left 0.15s" }} />
  </button>
);

const AnalyticsCharts = ({ data }) => {
  if (!data) return <div style={{ color: "var(--text-secondary)" }}><Loader2 size={15} className="spin" /> Loading…</div>;
  const mix = (data.metrics || METRICS).map((m) => ({ name: METRIC_LABELS[m], value: data.totals?.[m] || 0, key: m })).filter((x) => x.value > 0);
  return (
    <div data-testid="agency-analytics-charts">
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: "0.8rem", marginBottom: "1.3rem" }}>
        <div className="card" data-testid="an-placed" style={{ padding: "1rem", borderColor: "rgba(99,102,241,0.35)" }}>
          <div style={{ fontSize: "0.76rem", color: "var(--text-muted)" }}>Candidates placed</div>
          <div style={{ fontSize: "1.7rem", fontWeight: 800, marginTop: 2, color: "#6366f1" }}>{data.candidates_placed ?? 0}</div>
        </div>
        {(data.metrics || METRICS).map((m) => (
          <div key={m} className="card" data-testid={`an-kpi-${m}`} style={{ padding: "1rem" }}>
            <div style={{ fontSize: "0.76rem", color: "var(--text-muted)" }}>{METRIC_LABELS[m]}</div>
            <div style={{ fontSize: "1.7rem", fontWeight: 700, marginTop: 2, color: CHART_COLORS[m] }}>{data.totals?.[m] ?? 0}</div>
          </div>
        ))}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "1rem" }}>
        <div className="card" data-testid="an-funnel" style={{ padding: "1.2rem" }}>
          <h3 style={{ marginTop: 0, fontSize: "0.95rem" }}>Hiring funnel</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data.funnel || []} layout="vertical" margin={{ left: 20 }}>
              <XAxis type="number" tick={{ fontSize: 11 }} /><YAxis type="category" dataKey="stage" tick={{ fontSize: 11 }} width={80} />
              <Tooltip /><Bar dataKey="value" radius={[0, 6, 6, 0]}>
                {(data.funnel || []).map((f) => <Cell key={f.key} fill={CHART_COLORS[f.key] || "#6366f1"} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        {mix.length > 0 && (
          <div className="card" data-testid="an-pie" style={{ padding: "1.2rem" }}>
            <h3 style={{ marginTop: 0, fontSize: "0.95rem" }}>Activity mix</h3>
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={mix} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                  {mix.map((x) => <Cell key={x.key} fill={CHART_COLORS[x.key]} />)}
                </Pie>
                <Tooltip /><Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
      {(data.by_team || []).length > 0 && (
        <div className="card" data-testid="an-by-team" style={{ padding: "1.2rem", marginTop: "1rem" }}>
          <h3 style={{ marginTop: 0, fontSize: "0.95rem" }}>Applications & offers by team</h3>
          <ResponsiveContainer width="100%" height={Math.max(200, data.by_team.length * 50)}>
            <BarChart data={data.by_team} layout="vertical" margin={{ left: 30 }}>
              <XAxis type="number" tick={{ fontSize: 11 }} /><YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={110} />
              <Tooltip /><Legend />
              <Bar dataKey="applications" name="Applications" fill="#6366f1" radius={[0, 6, 6, 0]} />
              <Bar dataKey="offers" name="Offers" fill="#10b981" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

const chip = (active) => ({
  padding: "0.42rem 0.9rem", borderRadius: 999, fontSize: "0.82rem", cursor: "pointer",
  border: `1.5px solid ${active ? "#6366f1" : "var(--border-main)"}`,
  background: active ? "rgba(99,102,241,0.15)" : "transparent",
  color: active ? "#6366f1" : "var(--text-secondary)",
  fontWeight: active ? 600 : 400, transition: "all 0.15s",
  display: "inline-flex", alignItems: "center", gap: 6,
});

export default function AgencyDashboardPage() {
  const { agencyId } = useParams();
  const navigate = useNavigate();
  const [agency, setAgency] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [range, setRange] = useState("week");

  const [overview, setOverview] = useState(null);
  const [candidates, setCandidates] = useState(null);
  const [invites, setInvites] = useState([]);
  const [inviteUser, setInviteUser] = useState("");
  const [inviteRole, setInviteRole] = useState("candidate");
  const [inviting, setInviting] = useState(false);
  const [drill, setDrill] = useState(null);
  const [edit, setEdit] = useState(null);
  const [saving, setSaving] = useState(false);

  const [teams, setTeams] = useState([]);
  const [recruiters, setRecruiters] = useState([]);
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [teamForm, setTeamForm] = useState({ name: "", description: "", head_user_id: "" });
  const [policy, setPolicy] = useState(false);
  const [reportFreq, setReportFreq] = useState("off");
  const [sendingReport, setSendingReport] = useState(false);

  const [joinRequests, setJoinRequests] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [anRange, setAnRange] = useState("all");
  const [pp, setPp] = useState(null);
  const [intake, setIntake] = useState([]);
  const [savingPP, setSavingPP] = useState(false);
  const [candidateView, setCandidateView] = useState(null);
  const [mv, setMv] = useState(null);

  const isAdmin = agency?.my_role === "admin";
  const isRecruiter = agency?.my_role === "recruiter";

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/agencies/${agencyId}`);
      setAgency(res.data);
      setEdit({ name: res.data.name, handle: res.data.handle, description: res.data.description || "" });
      setPolicy(!!res.data.policies?.team_heads_see_org_totals);
      setReportFreq(res.data.report_config?.frequency || "off");
      if (res.data.public_profile) setPp(res.data.public_profile);
      if (res.data.member_visibility) setMv(res.data.member_visibility);
      if (res.data.intake_form) setIntake(res.data.intake_form);
    } catch (err) {
      try {
        const r = await axios.get(`${API}/agencies/${agencyId}/my-view`);
        setCandidateView(r.data);
      } catch {
        toast.error(err.response?.data?.detail || "You can't open this agency");
        navigate("/app/agencies");
      }
    } finally { setLoading(false); }
  }, [agencyId, navigate]);
  useEffect(() => { load(); }, [load]);

  const loadOverview = useCallback(async () => {
    try { const r = await axios.get(`${API}/agencies/${agencyId}/dashboard?range=${range}`); setOverview(r.data); }
    catch { /* ignore */ }
  }, [agencyId, range]);
  const loadCandidates = useCallback(async () => {
    try { const r = await axios.get(`${API}/agencies/${agencyId}/candidates?range=${range}`); setCandidates(r.data.candidates || []); }
    catch { /* ignore */ }
  }, [agencyId, range]);
  const loadInvites = useCallback(async () => {
    try { const r = await axios.get(`${API}/agencies/${agencyId}/invites`); setInvites(r.data.invites || []); }
    catch { /* ignore */ }
  }, [agencyId]);
  const loadTeams = useCallback(async () => {
    try { const r = await axios.get(`${API}/agencies/${agencyId}/teams`); setTeams(r.data.teams || []); }
    catch { /* ignore */ }
  }, [agencyId]);
  const loadRecruiters = useCallback(async () => {
    try { const r = await axios.get(`${API}/agencies/${agencyId}/recruiters`); setRecruiters(r.data.recruiters || []); }
    catch { /* ignore */ }
  }, [agencyId]);
  const loadRequests = useCallback(async () => {
    try { const r = await axios.get(`${API}/agencies/${agencyId}/join-requests`); setJoinRequests(r.data.requests || []); }
    catch { /* ignore */ }
  }, [agencyId]);
  const loadAnalytics = useCallback(async (rng) => {
    try { const r = await axios.get(`${API}/agencies/${agencyId}/analytics?range=${rng || anRange}`); setAnalytics(r.data); }
    catch { /* ignore */ }
  }, [agencyId, anRange]);

  useEffect(() => {
    if (!agency) return;
    if (isRecruiter) { loadTeams(); if (agency.policies?.team_heads_see_org_totals) loadOverview(); loadAnalytics(); return; }
    if (tab === "overview") loadOverview();
    if (tab === "candidates") loadCandidates();
    if (tab === "invites") loadInvites();
    if (tab === "teams") { loadTeams(); loadRecruiters(); }
    if (tab === "requests") loadRequests();
    if (tab === "analytics") loadAnalytics();
  }, [agency, isRecruiter, tab, range, loadOverview, loadCandidates, loadInvites, loadTeams, loadRecruiters, loadRequests, loadAnalytics]);

  const doInvite = async () => {
    const u = inviteUser.trim().replace(/^@/, "");
    if (!u) { toast.error("Enter a @username"); return; }
    setInviting(true);
    try {
      await axios.post(`${API}/agencies/${agencyId}/invite`, { username: u, role: inviteRole });
      toast.success(`Invited @${u} as ${inviteRole}`);
      setInviteUser(""); loadInvites(); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to invite"); }
    finally { setInviting(false); }
  };
  const cancelInvite = async (inv) => {
    try { await axios.post(`${API}/agencies/${agencyId}/invites/${inv.id}/cancel`); toast.success("Invite cancelled"); loadInvites(); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const removeMember = async (c, label) => {
    if (!window.confirm(`Remove ${c.name || c.username} from the agency?`)) return;
    try { await axios.post(`${API}/agencies/${agencyId}/members/${c.user_id}/remove`); toast.success(`${label} removed`); loadCandidates(); loadRecruiters(); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const openDrill = async (c) => {
    setDrill({ loading: true });
    try { const r = await axios.get(`${API}/agencies/${agencyId}/candidates/${c.user_id}?range=${range}`); setDrill({ ...r.data, loading: false }); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); setDrill(null); }
  };
  const createTeam = async () => {
    if (teamForm.name.trim().length < 2) { toast.error("Team name too short"); return; }
    try {
      await axios.post(`${API}/agencies/${agencyId}/teams`, { ...teamForm, head_user_id: teamForm.head_user_id || null });
      toast.success("Team created");
      setShowTeamModal(false); setTeamForm({ name: "", description: "", head_user_id: "" });
      loadTeams(); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const saveSettings = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/agencies/${agencyId}`, edit);
      setAgency((a) => ({ ...a, ...res.data }));
      toast.success("Agency updated");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };
  const savePolicy = async (val) => {
    setPolicy(val);
    try { await axios.put(`${API}/agencies/${agencyId}`, { policies: { team_heads_see_org_totals: val } }); toast.success("Policy updated"); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); setPolicy(!val); }
  };
  const saveReportFreq = async (val) => {
    setReportFreq(val);
    try { await axios.put(`${API}/agencies/${agencyId}`, { report_config: { frequency: val } }); toast.success(`Reports: ${val}`); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const sendReportNow = async () => {
    setSendingReport(true);
    try { const r = await axios.post(`${API}/agencies/${agencyId}/reports/send`); toast.success(`Report sent (${r.data.sent} email${r.data.sent !== 1 ? "s" : ""})`); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setSendingReport(false); }
  };
  const doDelete = async () => {
    if (!window.confirm("Delete this agency permanently? All candidates, recruiters, teams and invites will be removed.")) return;
    try { await axios.delete(`${API}/agencies/${agencyId}`); toast.success("Agency deleted"); navigate("/app/agencies"); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const savePolicyFlag = async (k, val) => {
    setAgency((a) => ({ ...a, policies: { ...a.policies, [k]: val } }));
    try { await axios.put(`${API}/agencies/${agencyId}`, { policies: { [k]: val } }); toast.success("Permission updated"); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); setAgency((a) => ({ ...a, policies: { ...a.policies, [k]: !val } })); }
  };
  const approveReq = async (r) => {
    try { await axios.post(`${API}/agencies/${agencyId}/join-requests/${r.id}/approve`); toast.success(`${r.name || r.username} approved`); loadRequests(); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const rejectReq = async (r) => {
    try { await axios.post(`${API}/agencies/${agencyId}/join-requests/${r.id}/reject`); toast.success("Request rejected"); loadRequests(); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const savePublicProfile = async () => {
    setSavingPP(true);
    try {
      await axios.put(`${API}/agencies/${agencyId}`, { public_profile: pp, intake_form: intake });
      toast.success("Public profile & intake form saved");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setSavingPP(false); }
  };
  const togglePPMetric = (m) => {
    setPp((p) => ({ ...p, metrics: p.metrics.includes(m) ? p.metrics.filter((x) => x !== m) : [...p.metrics, m] }));
  };
  const saveMemberVisibility = async (patch) => {
    const next = { ...mv, ...patch };
    setMv(next);
    try { await axios.put(`${API}/agencies/${agencyId}`, { member_visibility: next }); }
    catch { setMv(mv); toast.error("Failed to save member view"); }
  };
  const toggleMvMetric = (m) => {
    const has = mv.metrics?.includes(m);
    saveMemberVisibility({ metrics: has ? mv.metrics.filter((x) => x !== m) : [...(mv.metrics || []), m] });
  };
  const addField = () => setIntake((f) => [...f, { id: `f${Date.now()}`, label: "", type: "text", required: false, options: [] }]);
  const updField = (i, patch) => setIntake((f) => f.map((x, j) => j === i ? { ...x, ...patch } : x));
  const delField = (i) => setIntake((f) => f.filter((_, j) => j !== i));

  if (loading) return <div className="analytics-loading" data-testid="agency-dashboard-loading"><div className="processing-spinner" /></div>;
  if (candidateView) return <CandidateAgencyView data={candidateView} />;
  if (!agency) return null;

  // ── RECRUITER (Team Head) VIEW ──
  if (isRecruiter) {
    return (
      <div className="page-container fade-in" data-testid="recruiter-dashboard-page">
        <button className="btn-secondary" data-testid="agency-back-btn" onClick={() => navigate("/app/agencies")}><ArrowLeft size={16} /> Agencies</button>
        <div className="page-header" style={{ marginTop: "0.8rem" }}>
          <div>
            <h1 style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Building2 size={22} /> {agency.name}
              <span style={{ fontSize: "0.66rem", padding: "2px 8px", borderRadius: 999, background: "rgba(16,185,129,0.15)", color: "#10b981", display: "inline-flex", alignItems: "center", gap: 3 }}><UserCog size={11} /> Recruiter</span>
            </h1>
            <p style={{ color: "var(--text-secondary)" }}>@{agency.handle} · You lead {teams.length} team{teams.length !== 1 ? "s" : ""} here.</p>
          </div>
        </div>

        {agency.policies?.team_heads_see_org_totals && overview && (
          <div className="card" data-testid="recruiter-org-totals" style={{ padding: "1.1rem 1.3rem", marginBottom: "1.2rem" }}>
            <div style={{ fontSize: "0.8rem", color: "var(--text-secondary)", marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}><Network size={15} /> Agency-wide totals (read-only)</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "1.4rem" }}>
              {METRICS.map((m) => <div key={m}><span style={{ fontSize: "1.3rem", fontWeight: 700 }}>{overview.totals?.[m] ?? 0}</span> <span style={{ fontSize: "0.76rem", color: "var(--text-muted)" }}>{METRIC_LABELS[m]}</span></div>)}
            </div>
          </div>
        )}

        <div className="card" data-testid="recruiter-placements" style={{ padding: "1.1rem 1.3rem", marginBottom: "1.2rem", display: "flex", alignItems: "center", gap: 12, borderColor: "rgba(16,185,129,0.35)" }}>
          <Award size={22} style={{ color: "#10b981" }} />
          <div><span style={{ fontSize: "1.5rem", fontWeight: 800, color: "#10b981" }}>{analytics?.placements_total ?? 0}</span> <span style={{ color: "var(--text-secondary)", fontSize: "0.85rem" }}>candidates you've helped land a job (credit stays even after they leave your team)</span></div>
        </div>

        <h3 style={{ display: "flex", alignItems: "center", gap: 6 }}><Users size={18} /> Your teams</h3>
        {teams.length === 0 ? (
          <div className="card" data-testid="recruiter-no-teams" style={{ padding: "2rem", textAlign: "center", color: "var(--text-secondary)" }}>
            <Users size={32} style={{ opacity: 0.35 }} />
            <p>You're not heading any team yet. The agency admin will assign you one.</p>
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: "1rem" }}>
            {teams.map((t) => (
              <div key={t.id} className="card" data-testid={`recruiter-team-${t.id}`} onClick={() => navigate(`/app/agencies/${agencyId}/teams/${t.id}`)} style={{ padding: "1.3rem", cursor: "pointer" }}>
                <div style={{ width: 42, height: 42, borderRadius: 12, background: "rgba(99,102,241,0.15)", color: "#6366f1", display: "flex", alignItems: "center", justifyContent: "center" }}><Users size={22} /></div>
                <h3 style={{ margin: "0.7rem 0 0.2rem" }}>{t.name}</h3>
                <div style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>{t.candidate_count} candidate{t.candidate_count !== 1 ? "s" : ""}</div>
                <div style={{ marginTop: 12, color: "#6366f1", fontSize: "0.84rem", display: "flex", alignItems: "center", gap: 4 }}>Open team <ChevronRight size={14} /></div>
              </div>
            ))}
          </div>
        )}

        <div style={{ marginTop: "1.6rem" }}>
          <h3 style={{ display: "flex", alignItems: "center", gap: 6 }}><BarChart3 size={18} /> Your team analytics</h3>
          <AnalyticsCharts data={analytics} />
        </div>
      </div>
    );
  }

  // ── ADMIN VIEW ──
  const tabs = [
    { k: "overview", l: "Overview", icon: LayoutDashboard },
    { k: "analytics", l: "Analytics", icon: BarChart3 },
    { k: "candidates", l: `Candidates (${agency.candidate_count})`, icon: Users },
    { k: "teams", l: `Teams (${agency.team_count ?? 0})`, icon: Network },
    { k: "requests", l: `Requests${agency.pending_requests ? ` (${agency.pending_requests})` : ""}`, icon: Inbox },
    { k: "invites", l: `Invites${agency.pending_invites ? ` (${agency.pending_invites})` : ""}`, icon: Mail },
    { k: "permissions", l: "Permissions", icon: ShieldCheck },
    { k: "settings", l: "Settings", icon: Cog },
  ];

  return (
    <div className="page-container fade-in" data-testid="agency-dashboard-page">
      <button className="btn-secondary" data-testid="agency-back-btn" onClick={() => navigate("/app/agencies")}><ArrowLeft size={16} /> Agencies</button>

      <div className="page-header" style={{ marginTop: "0.8rem" }}>
        <div>
          <h1 style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Building2 size={22} /> {agency.name}
            <span style={{ fontSize: "0.66rem", padding: "2px 8px", borderRadius: 999, background: "rgba(245,158,11,0.15)", color: "#f59e0b", display: "inline-flex", alignItems: "center", gap: 3 }}><Crown size={11} /> Admin</span>
          </h1>
          <p style={{ color: "var(--text-secondary)" }}>@{agency.handle}{agency.description ? ` · ${agency.description}` : ""}</p>
        </div>
      </div>

      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", borderBottom: "1px solid var(--border-main)", marginBottom: "1.2rem" }}>
        {tabs.map(({ k, l, icon: Icon }) => (
          <button key={k} data-testid={`agency-tab-${k}`} onClick={() => setTab(k)}
            style={{ background: "none", border: "none", borderBottom: `2px solid ${tab === k ? "#6366f1" : "transparent"}`,
              color: tab === k ? "var(--text-primary)" : "var(--text-secondary)", padding: "0.6rem 0.9rem", cursor: "pointer",
              fontWeight: tab === k ? 600 : 400, display: "flex", alignItems: "center", gap: 6, fontSize: "0.88rem" }}>
            <Icon size={15} /> {l}
          </button>
        ))}
      </div>

      {(tab === "overview" || tab === "candidates") && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: "1rem" }} data-testid="agency-range-selector">
          {RANGES.map((r) => (
            <button key={r.k} data-testid={`agency-range-${r.k}`} onClick={() => setRange(r.k)} style={chip(range === r.k)}>{r.l}</button>
          ))}
        </div>
      )}

      {/* ANALYTICS */}
      {tab === "analytics" && (
        <div data-testid="agency-analytics-tab">
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: "1rem" }}>
            {RANGES.map((r) => (
              <button key={r.k} data-testid={`an-range-${r.k}`} onClick={() => { setAnRange(r.k); loadAnalytics(r.k); }} style={chip(anRange === r.k)}>{r.l}</button>
            ))}
          </div>
          <AnalyticsCharts data={analytics} />
        </div>
      )}

      {/* REQUESTS */}
      {tab === "requests" && (
        <div data-testid="agency-requests-tab">
          <h3 style={{ display: "flex", alignItems: "center", gap: 6 }}><Inbox size={18} /> Join requests</h3>
          <p style={{ fontSize: "0.83rem", color: "var(--text-secondary)", marginTop: 0 }}>Candidates who searched your agency and applied with the intake form. Approve to add them (with consent to share stats).</p>
          {joinRequests.length === 0 ? (
            <div className="card" data-testid="requests-empty" style={{ padding: "2rem", textAlign: "center", color: "var(--text-secondary)" }}>
              <Inbox size={32} style={{ opacity: 0.35 }} /><p>No pending join requests.</p>
            </div>
          ) : joinRequests.map((r) => (
            <div key={r.id} className="card" data-testid={`join-request-${r.username || r.id}`} style={{ padding: "1.1rem 1.3rem", marginBottom: 10 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
                <div>
                  <div style={{ fontWeight: 600 }}>{r.name || r.username} <span style={{ fontSize: "0.74rem", color: "var(--text-muted)" }}>@{r.username}</span></div>
                  <div style={{ fontSize: "0.72rem", color: "var(--text-muted)" }}>{new Date(r.created_at).toLocaleString()}</div>
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  <button className="btn-primary" data-testid={`approve-request-${r.username || r.id}`} onClick={() => approveReq(r)} style={{ padding: "0.4rem 0.8rem" }}><Check size={14} /> Approve</button>
                  <button className="btn-secondary" data-testid={`reject-request-${r.username || r.id}`} onClick={() => rejectReq(r)} style={{ color: "#ef4444", padding: "0.4rem 0.7rem" }}><X size={14} /></button>
                </div>
              </div>
              <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 8 }}>
                {Object.entries(r.responses || {}).map(([fid, f]) => (
                  <div key={fid} style={{ fontSize: "0.82rem" }}><span style={{ color: "var(--text-muted)" }}>{f.label}: </span><strong>{f.value || "—"}</strong></div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* PERMISSIONS */}
      {tab === "permissions" && (
        <div data-testid="agency-permissions-tab">
          <div className="card" style={{ padding: "1.3rem" }}>
            <h3 style={{ marginTop: 0, display: "flex", alignItems: "center", gap: 6 }}><ShieldCheck size={18} /> Recruiter / Team-Head permissions</h3>
            <p style={{ fontSize: "0.83rem", color: "var(--text-secondary)", marginTop: 0 }}>Control what your recruiters (Team Heads) can see and do. They always manage their own team.</p>
            {POLICY_FLAGS.map((f, i) => (
              <div key={f.k} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 14, padding: "0.85rem 0", borderTop: i ? "1px solid var(--border-main)" : "none" }}>
                <div>
                  <div style={{ fontWeight: 500, fontSize: "0.9rem" }}>{f.l}</div>
                  <div style={{ fontSize: "0.8rem", color: "var(--text-secondary)" }}>{f.d}</div>
                </div>
                <Toggle on={!!agency.policies?.[f.k]} onClick={() => savePolicyFlag(f.k, !agency.policies?.[f.k])} testid={`perm-${f.k}`} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* OVERVIEW */}
      {tab === "overview" && (
        <div data-testid="agency-overview-tab">
          {!overview ? <div style={{ color: "var(--text-secondary)" }}><Loader2 size={15} className="spin" /> Loading…</div> : (
            <>
              <div className="card" data-testid="agency-placed" style={{ padding: "1rem 1.3rem", marginBottom: "1rem", display: "flex", alignItems: "center", gap: 12, borderColor: "rgba(99,102,241,0.35)" }}>
                <Award size={22} style={{ color: "#6366f1" }} />
                <div><span style={{ fontSize: "1.5rem", fontWeight: 800, color: "#6366f1" }}>{overview.candidates_placed}</span> <span style={{ color: "var(--text-secondary)", fontSize: "0.85rem" }}>candidates placed (with an offer)</span></div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: "0.8rem", marginBottom: "1.5rem" }}>
                {METRICS.map((m) => (
                  <div key={m} className="card" data-testid={`agency-total-${m}`} style={{ padding: "1.1rem" }}>
                    <div style={{ fontSize: "0.78rem", color: "var(--text-muted)" }}>{METRIC_LABELS[m]}</div>
                    <div style={{ fontSize: "1.8rem", fontWeight: 700, marginTop: 4 }}>{overview.totals?.[m] ?? 0}</div>
                  </div>
                ))}
              </div>
              <h3 style={{ display: "flex", alignItems: "center", gap: 6 }}><Trophy size={18} /> Top performers</h3>
              {(!overview.top_performers || overview.top_performers.length === 0) ? (
                <div className="card" data-testid="agency-overview-empty" style={{ padding: "2rem", textAlign: "center", color: "var(--text-secondary)" }}>
                  <Users size={32} style={{ opacity: 0.35 }} />
                  <p>No consented candidates yet. Invite candidates and ask them to consent to share stats.</p>
                </div>
              ) : (
                <div className="card" style={{ padding: 0 }}>
                  {overview.top_performers.map((p, i) => (
                    <div key={p.user_id} data-testid={`top-performer-${p.username}`}
                      style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0.8rem 1.1rem", borderTop: i ? "1px solid var(--border-main)" : "none" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <span style={{ fontWeight: 700, width: 22 }}>{i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : i + 1}</span>
                        <div>
                          <div style={{ fontWeight: 500 }}>{p.name || p.username}</div>
                          <div style={{ fontSize: "0.74rem", color: "var(--text-muted)" }}>@{p.username}</div>
                        </div>
                      </div>
                      <div style={{ fontSize: "0.82rem", color: "var(--text-secondary)" }}>
                        <strong style={{ color: "var(--text-primary)" }}>{p.applications}</strong> applications · <strong style={{ color: "#10b981" }}>{p.offers}</strong> offers
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* CANDIDATES */}
      {tab === "candidates" && (
        <div data-testid="agency-candidates-tab">
          {!candidates ? <div style={{ color: "var(--text-secondary)" }}><Loader2 size={15} className="spin" /> Loading…</div>
            : candidates.length === 0 ? (
              <div className="card" data-testid="agency-candidates-empty" style={{ padding: "2rem", textAlign: "center", color: "var(--text-secondary)" }}>
                <Users size={32} style={{ opacity: 0.35 }} />
                <p>No consented candidates. Only candidates who accept your invite and consent appear here.</p>
              </div>
            ) : (
              <div className="card" style={{ padding: 0, overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.88rem" }} data-testid="agency-candidates-table">
                  <thead>
                    <tr style={{ textAlign: "left", color: "var(--text-secondary)", fontSize: "0.78rem" }}>
                      <th style={{ padding: "0.8rem 1rem" }}>Candidate</th>
                      {METRICS.map((m) => <th key={m} style={{ padding: "0.8rem 1rem", textAlign: "right" }}>{METRIC_LABELS[m]}</th>)}
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {candidates.map((c) => (
                      <tr key={c.user_id} data-testid={`agency-candidate-${c.username}`} style={{ borderTop: "1px solid var(--border-main)", cursor: "pointer" }} onClick={() => openDrill(c)}>
                        <td style={{ padding: "0.7rem 1rem" }}>
                          <div style={{ fontWeight: 500 }}>{c.name || c.username}</div>
                          <div style={{ fontSize: "0.74rem", color: "var(--text-muted)" }}>@{c.username}{c.team_name ? <span style={{ color: "#f59e0b" }}> · {c.team_name}</span> : ""}</div>
                        </td>
                        {METRICS.map((m) => <td key={m} style={{ padding: "0.7rem 1rem", textAlign: "right" }}>{c.metrics?.[m] ?? 0}</td>)}
                        <td style={{ padding: "0.7rem 1rem", display: "flex", gap: 6, justifyContent: "flex-end" }}>
                          <button className="btn-secondary" data-testid={`view-candidate-${c.username}`} onClick={(e) => { e.stopPropagation(); openDrill(c); }} style={{ padding: "0.3rem 0.6rem" }}><ChevronRight size={14} /></button>
                          <button className="btn-secondary" data-testid={`remove-candidate-${c.username}`} onClick={(e) => { e.stopPropagation(); removeMember(c, "Candidate"); }} style={{ padding: "0.3rem 0.6rem", color: "#ef4444" }}><UserMinus size={14} /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
        </div>
      )}

      {/* TEAMS */}
      {tab === "teams" && (
        <div data-testid="agency-teams-tab">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.8rem" }}>
            <h3 style={{ margin: 0, display: "flex", alignItems: "center", gap: 6 }}><Network size={18} /> Teams</h3>
            <button className="btn-primary" data-testid="new-team-btn" onClick={() => setShowTeamModal(true)} disabled={recruiters.length === 0} title={recruiters.length === 0 ? "Invite a recruiter first" : ""}><UserPlus size={15} /> New team</button>
          </div>
          {recruiters.length === 0 && (
            <div className="card" data-testid="no-recruiters-hint" style={{ padding: "1rem 1.2rem", marginBottom: "1rem", color: "var(--text-secondary)", fontSize: "0.85rem" }}>
              Teams are led by <strong>recruiters</strong> (Team Heads). Invite a recruiter from the <strong>Invites</strong> tab (set role to “Recruiter”), then create a team and assign them as head.
            </div>
          )}
          {teams.length === 0 ? (
            <div className="card" data-testid="teams-empty" style={{ padding: "2rem", textAlign: "center", color: "var(--text-secondary)" }}>
              <Network size={32} style={{ opacity: 0.35 }} />
              <p>No teams yet. Create a team and assign a recruiter as its head.</p>
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: "1rem", marginBottom: "1.5rem" }}>
              {teams.map((t) => (
                <div key={t.id} className="card" data-testid={`team-card-${t.id}`} onClick={() => navigate(`/app/agencies/${agencyId}/teams/${t.id}`)} style={{ padding: "1.3rem", cursor: "pointer" }}>
                  <div style={{ width: 42, height: 42, borderRadius: 12, background: "rgba(99,102,241,0.15)", color: "#6366f1", display: "flex", alignItems: "center", justifyContent: "center" }}><Users size={22} /></div>
                  <h3 style={{ margin: "0.7rem 0 0.2rem" }}>{t.name}</h3>
                  <div style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>{t.candidate_count} candidate{t.candidate_count !== 1 ? "s" : ""}</div>
                  <div style={{ fontSize: "0.78rem", color: t.head ? "#10b981" : "var(--text-muted)", marginTop: 4, display: "flex", alignItems: "center", gap: 4 }}>
                    <UserCog size={12} /> {t.head ? `${t.head.name}` : "No head assigned"}
                  </div>
                  <div style={{ marginTop: 12, color: "#6366f1", fontSize: "0.84rem", display: "flex", alignItems: "center", gap: 4 }}>Open team <ChevronRight size={14} /></div>
                </div>
              ))}
            </div>
          )}

          <h3 style={{ display: "flex", alignItems: "center", gap: 6 }}><UserCog size={18} /> Recruiters</h3>
          {recruiters.length === 0 ? (
            <div className="card" data-testid="recruiters-empty" style={{ padding: "1.4rem", textAlign: "center", color: "var(--text-secondary)" }}>No recruiters yet. Invite one from the Invites tab.</div>
          ) : recruiters.map((r) => (
            <div key={r.user_id} className="card" data-testid={`recruiter-${r.username}`} style={{ padding: "0.8rem 1.1rem", display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <div>
                <div style={{ fontWeight: 500 }}>{r.name || r.username} <span style={{ fontSize: "0.74rem", color: "var(--text-muted)" }}>@{r.username}</span></div>
                <div style={{ fontSize: "0.76rem", color: "var(--text-secondary)" }}>{r.teams.length ? `Heads: ${r.teams.map((t) => t.name).join(", ")}` : "Not assigned to a team"}</div>
              </div>
              <button className="btn-secondary" data-testid={`remove-recruiter-${r.username}`} onClick={() => removeMember(r, "Recruiter")} style={{ color: "#ef4444" }}><UserMinus size={14} /></button>
            </div>
          ))}
        </div>
      )}

      {/* INVITES */}
      {tab === "invites" && (
        <div data-testid="agency-invites-tab">
          <div className="card" style={{ padding: "1.3rem", marginBottom: "1rem" }}>
            <h3 style={{ marginTop: 0, display: "flex", alignItems: "center", gap: 6 }}><UserPlus size={18} /> Invite</h3>
            <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
              {[{ k: "candidate", l: "Candidate" }, { k: "recruiter", l: "Recruiter (Team Head)" }].map((r) => (
                <button key={r.k} data-testid={`invite-role-${r.k}`} onClick={() => setInviteRole(r.k)} style={chip(inviteRole === r.k)}>{r.l}</button>
              ))}
            </div>
            <p style={{ fontSize: "0.83rem", color: "var(--text-secondary)", marginTop: 0 }}>
              {inviteRole === "candidate"
                ? "Candidates must accept and consent before their stats appear."
                : "Recruiters lead a team. They join without sharing personal stats and are measured by their team's results."}
            </p>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <input className="modal-input" data-testid="agency-invite-input" value={inviteUser}
                onChange={(e) => setInviteUser(e.target.value)} onKeyDown={(e) => e.key === "Enter" && doInvite()}
                placeholder="@username" style={{ flex: 1, minWidth: 200 }} />
              <button className="btn-primary" data-testid="agency-invite-btn" onClick={doInvite} disabled={inviting}>
                {inviting ? <Loader2 size={16} className="spin" /> : <UserPlus size={16} />} Invite
              </button>
            </div>
          </div>

          <h3>Pending invites</h3>
          {invites.length === 0 ? (
            <div className="card" data-testid="agency-invites-empty" style={{ padding: "2rem", textAlign: "center", color: "var(--text-secondary)" }}>No pending invites.</div>
          ) : invites.map((inv) => (
            <div key={inv.id} className="card" data-testid={`pending-invite-${inv.username}`} style={{ padding: "0.8rem 1.1rem", display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <div>
                <div style={{ fontWeight: 500 }}>{inv.name || inv.username} <span style={{ fontSize: "0.66rem", padding: "1px 7px", borderRadius: 999, background: inv.role === "recruiter" ? "rgba(16,185,129,0.15)" : "rgba(99,102,241,0.15)", color: inv.role === "recruiter" ? "#10b981" : "#6366f1" }}>{inv.role}</span></div>
                <div style={{ fontSize: "0.76rem", color: "var(--text-muted)" }}>@{inv.username}</div>
              </div>
              <button className="btn-secondary" data-testid={`cancel-invite-${inv.username}`} onClick={() => cancelInvite(inv)} style={{ color: "#ef4444" }}><X size={14} /> Cancel</button>
            </div>
          ))}
        </div>
      )}

      {/* SETTINGS */}
      {tab === "settings" && edit && (
        <div data-testid="agency-settings-tab">
          <div className="card" style={{ padding: "1.3rem", marginBottom: "1rem" }}>
            <h3 style={{ marginTop: 0 }}>Details</h3>
            <label style={{ fontSize: "0.82rem", color: "var(--text-secondary)" }}>Agency name</label>
            <input className="modal-input" data-testid="agency-edit-name" value={edit.name} maxLength={80} onChange={(e) => setEdit({ ...edit, name: e.target.value })} style={{ width: "100%", marginBottom: 10 }} />
            <label style={{ fontSize: "0.82rem", color: "var(--text-secondary)" }}>Handle (@)</label>
            <input className="modal-input" data-testid="agency-edit-handle" value={edit.handle} onChange={(e) => setEdit({ ...edit, handle: e.target.value.toLowerCase() })} style={{ width: "100%", marginBottom: 10 }} />
            <label style={{ fontSize: "0.82rem", color: "var(--text-secondary)" }}>Description</label>
            <textarea className="modal-input" data-testid="agency-edit-desc" value={edit.description} rows={2} maxLength={300} onChange={(e) => setEdit({ ...edit, description: e.target.value })} style={{ width: "100%", marginBottom: 14, resize: "vertical" }} />
            <button className="btn-primary" data-testid="agency-save-settings" onClick={saveSettings} disabled={saving}>
              {saving ? <Loader2 size={16} className="spin" /> : <Check size={16} />} Save changes
            </button>
          </div>

          {/* Public profile */}
          <div className="card" style={{ padding: "1.3rem", marginBottom: "1rem" }}>
            <h3 style={{ marginTop: 0, display: "flex", alignItems: "center", gap: 6 }}><Globe size={18} /> Public profile</h3>
            <p style={{ fontSize: "0.83rem", color: "var(--text-secondary)", marginTop: 0 }}>Let candidates discover your agency in search and request to join. Choose what's shown publicly.</p>
            {pp && (
              <>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0.6rem 0" }}>
                  <div><div style={{ fontWeight: 500, fontSize: "0.9rem" }}>Listed in search</div><div style={{ fontSize: "0.8rem", color: "var(--text-secondary)" }}>When off, your agency is invite-only and hidden from search.</div></div>
                  <Toggle on={!!pp.visible} onClick={() => setPp({ ...pp, visible: !pp.visible })} testid="pp-visible" />
                </div>
                <label style={{ fontSize: "0.82rem", color: "var(--text-secondary)" }}>Tagline</label>
                <input className="modal-input" data-testid="pp-tagline" value={pp.tagline || ""} maxLength={160} onChange={(e) => setPp({ ...pp, tagline: e.target.value })} placeholder="e.g. We place top tech talent at startups" style={{ width: "100%", marginBottom: 12 }} />
                <div style={{ fontSize: "0.82rem", color: "var(--text-secondary)", marginBottom: 6 }}>Stats to show publicly</div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
                  {PUBLIC_METRICS.map((m) => (
                    <button key={m} data-testid={`pp-metric-${m}`} onClick={() => togglePPMetric(m)} style={chip(pp.metrics?.includes(m))}>{METRIC_LABELS[m]}</button>
                  ))}
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0.4rem 0" }}>
                  <div style={{ fontWeight: 500, fontSize: "0.88rem" }}>Show "candidates placed"</div>
                  <Toggle on={!!pp.show_placed} onClick={() => setPp({ ...pp, show_placed: !pp.show_placed })} testid="pp-show-placed" />
                </div>
              </>
            )}
          </div>

          {/* What members see */}
          {mv && (
            <div className="card" data-testid="member-visibility-card" style={{ padding: "1.3rem", marginBottom: "1rem" }}>
              <h3 style={{ marginTop: 0, display: "flex", alignItems: "center", gap: 6 }}><Eye size={18} /> What members see</h3>
              <p style={{ fontSize: "0.83rem", color: "var(--text-secondary)", marginTop: 0 }}>Control the agency stats your joined candidates can see on their dashboard (separate from your public profile).</p>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0.5rem 0" }}>
                <div style={{ fontWeight: 500, fontSize: "0.9rem" }}>Show agency stats to members</div>
                <Toggle on={!!mv.visible} onClick={() => saveMemberVisibility({ visible: !mv.visible })} testid="mv-visible" />
              </div>
              {mv.visible && (
                <>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0.5rem 0", borderTop: "1px solid var(--border-main)" }}>
                    <div style={{ fontSize: "0.88rem" }}>Number of candidates</div>
                    <Toggle on={!!mv.show_member_count} onClick={() => saveMemberVisibility({ show_member_count: !mv.show_member_count })} testid="mv-member-count" />
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0.5rem 0", borderTop: "1px solid var(--border-main)" }}>
                    <div style={{ fontSize: "0.88rem" }}>Candidates placed (jobs landed)</div>
                    <Toggle on={!!mv.show_placed} onClick={() => saveMemberVisibility({ show_placed: !mv.show_placed })} testid="mv-show-placed" />
                  </div>
                  <div style={{ fontSize: "0.82rem", color: "var(--text-secondary)", margin: "10px 0 6px" }}>Aggregate metrics members can see</div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {PUBLIC_METRICS.map((m) => (
                      <button key={m} data-testid={`mv-metric-${m}`} onClick={() => toggleMvMetric(m)} style={chip(mv.metrics?.includes(m))}>{METRIC_LABELS[m]}</button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Intake form builder */}
          <div className="card" style={{ padding: "1.3rem", marginBottom: "1rem" }}>
            <h3 style={{ marginTop: 0, display: "flex", alignItems: "center", gap: 6 }}><Inbox size={18} /> Join intake form</h3>
            <p style={{ fontSize: "0.83rem", color: "var(--text-secondary)", marginTop: 0 }}>The details candidates fill in when they request to join. Drag-free, just edit below.</p>
            {intake.map((f, i) => (
              <div key={f.id} data-testid={`intake-field-${i}`} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8, flexWrap: "wrap" }}>
                <input className="modal-input" data-testid={`intake-label-${i}`} value={f.label} placeholder="Field label" onChange={(e) => updField(i, { label: e.target.value })} style={{ flex: 1, minWidth: 140 }} />
                <select className="modal-input" data-testid={`intake-type-${i}`} value={f.type} onChange={(e) => updField(i, { type: e.target.value })} style={{ width: 120 }}>
                  <option value="text">Text</option><option value="email">Email</option><option value="tel">Phone</option><option value="textarea">Long text</option>
                </select>
                <button onClick={() => updField(i, { required: !f.required })} data-testid={`intake-req-${i}`} style={chip(f.required)}>{f.required ? "Required" : "Optional"}</button>
                <button className="btn-secondary" data-testid={`intake-del-${i}`} onClick={() => delField(i)} style={{ color: "#ef4444", padding: "0.35rem 0.5rem" }}><Trash2 size={14} /></button>
              </div>
            ))}
            <button className="btn-secondary" data-testid="intake-add" onClick={addField} style={{ marginTop: 6 }}><Plus size={14} /> Add field</button>
            <div style={{ marginTop: 14 }}>
              <button className="btn-primary" data-testid="save-public-profile" onClick={savePublicProfile} disabled={savingPP}>
                {savingPP ? <Loader2 size={16} className="spin" /> : <Check size={16} />} Save public profile & intake form
              </button>
            </div>
          </div>

          {/* Scheduled reports */}
          <div className="card" style={{ padding: "1.3rem", marginBottom: "1rem" }}>
            <h3 style={{ marginTop: 0, display: "flex", alignItems: "center", gap: 6 }}><Mail size={18} /> Email reports</h3>
            <p style={{ fontSize: "0.83rem", color: "var(--text-secondary)", marginTop: 0 }}>Send recurring email reports — agency overview to admins, each team's report to its Team Head.</p>
            <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
              {FREQS.map((f) => <button key={f.k} data-testid={`report-freq-${f.k}`} onClick={() => saveReportFreq(f.k)} style={chip(reportFreq === f.k)}>{f.l}</button>)}
            </div>
            <button className="btn-secondary" data-testid="send-report-now" onClick={sendReportNow} disabled={sendingReport}>
              {sendingReport ? <Loader2 size={16} className="spin" /> : <Send size={15} />} Send report now
            </button>
          </div>

          {agency.is_creator && (
            <div className="card" style={{ padding: "1.3rem", borderColor: "rgba(239,68,68,0.4)" }}>
              <h3 style={{ marginTop: 0, color: "#ef4444" }}>Danger zone</h3>
              <button data-testid="agency-delete-btn" onClick={doDelete} className="btn-secondary" style={{ color: "#ef4444", borderColor: "rgba(239,68,68,0.5)" }}><Trash2 size={15} /> Delete agency</button>
            </div>
          )}
        </div>
      )}

      {/* CANDIDATE DRILL-DOWN */}
      {drill && (
        <div className="modal-overlay" data-testid="candidate-drill-modal" onClick={() => setDrill(null)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, padding: "1rem" }}>
          <div className="card" onClick={(e) => e.stopPropagation()} style={{ padding: "1.8rem", width: "min(520px, 100%)" }}>
            {drill.loading ? <div style={{ textAlign: "center", padding: "1rem" }}><Loader2 size={20} className="spin" /></div> : (
              <>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "1rem" }}>
                  <div>
                    <h2 style={{ margin: 0 }}>{drill.candidate.name || drill.candidate.username}</h2>
                    <div style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>@{drill.candidate.username} · {RANGES.find((r) => r.k === range)?.l}</div>
                  </div>
                  <button onClick={() => setDrill(null)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)" }}><X size={20} /></button>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "0.7rem" }}>
                  {METRICS.map((m) => (
                    <div key={m} data-testid={`drill-${m}`} style={{ padding: "0.9rem", borderRadius: 10, background: "var(--bg-surface-hl)" }}>
                      <div style={{ fontSize: "0.74rem", color: "var(--text-muted)" }}>{METRIC_LABELS[m]}</div>
                      <div style={{ fontSize: "1.4rem", fontWeight: 700, marginTop: 2 }}>{drill.stats?.[m] ?? 0}</div>
                    </div>
                  ))}
                </div>
                {drill.stats?.mostly_applied_hour && (
                  <div style={{ marginTop: 12, fontSize: "0.84rem", color: "var(--text-secondary)", display: "flex", alignItems: "center", gap: 6 }}>
                    <Clock size={14} /> Most active applying around <strong>{drill.stats.mostly_applied_hour}</strong>
                  </div>
                )}
                <button className="btn-secondary" data-testid="drill-view-profile" onClick={() => navigate(`/app/u/${drill.candidate.username}`)} style={{ marginTop: 16 }}>View public profile</button>
              </>
            )}
          </div>
        </div>
      )}

      {/* CREATE TEAM MODAL */}
      {showTeamModal && (
        <div className="modal-overlay" data-testid="create-team-modal" onClick={() => setShowTeamModal(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, padding: "1rem" }}>
          <div className="card" onClick={(e) => e.stopPropagation()} style={{ padding: "1.8rem", width: "min(460px, 100%)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
              <h2 style={{ margin: 0 }}>New team</h2>
              <button onClick={() => setShowTeamModal(false)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)" }}><X size={20} /></button>
            </div>
            <label style={{ fontSize: "0.82rem", color: "var(--text-secondary)" }}>Team name</label>
            <input className="modal-input" data-testid="team-name-input" value={teamForm.name} maxLength={80} onChange={(e) => setTeamForm({ ...teamForm, name: e.target.value })} placeholder="Frontend Pod" style={{ width: "100%", marginBottom: 12 }} />
            <label style={{ fontSize: "0.82rem", color: "var(--text-secondary)" }}>Description</label>
            <input className="modal-input" data-testid="team-desc-input" value={teamForm.description} maxLength={300} onChange={(e) => setTeamForm({ ...teamForm, description: e.target.value })} placeholder="What's this team for?" style={{ width: "100%", marginBottom: 12 }} />
            <label style={{ fontSize: "0.82rem", color: "var(--text-secondary)" }}>Team head (recruiter)</label>
            <select className="modal-input" data-testid="team-head-select" value={teamForm.head_user_id} onChange={(e) => setTeamForm({ ...teamForm, head_user_id: e.target.value })} style={{ width: "100%", marginBottom: 16 }}>
              <option value="">— No head yet —</option>
              {recruiters.map((r) => <option key={r.user_id} value={r.user_id}>{r.name || r.username} (@{r.username})</option>)}
            </select>
            <button className="btn-primary" data-testid="create-team-submit" onClick={createTeam} style={{ width: "100%" }}><UserPlus size={16} /> Create team</button>
          </div>
        </div>
      )}
    </div>
  );
}
